import React, { useMemo, useState } from 'react';
import { Select, Input, Typography } from 'antd';
import { formatPrice } from '../../utils/currencyUtils';
import axios from '../../services/axiosInstance';
import { debounce } from 'lodash';

const { Text } = Typography;

const ProductSelector = ({
  item,
  proposalCurrency,
  onProductSelect,
  isCustom,
  onItemChange,
  index,
}) => {
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        if (!inputValue || inputValue.trim().length < 2) {
          setOptions([]);
          setFetching(false);
          return;
        }

        setFetching(true);
        try {
          const response = await axios.get('/products', {
            params: {
              search: inputValue,
              limit: 20,
            },
          });

          const searchResults = response.data.products || [];
          setOptions(
            searchResults.map((product) => ({
              value: product.product_id,
              label: `${product.name} - ${formatPrice(product.base_price, proposalCurrency)}`,
              product,
            }))
          );
        } catch (error) {
          console.error('Error fetching products:', error);
          setOptions([]);
        } finally {
          setFetching(false);
        }
      }, 300),
    [proposalCurrency]
  );

  const selectedOption = useMemo(() => {
    if (item.product_id && item.item_name) {
      return {
        value: item.product_id,
        label: `${item.item_name} - ${formatPrice(item.list_price || 0, proposalCurrency)}`,
        product: {
          product_id: item.product_id,
          name: item.item_name,
          base_price: item.list_price || 0,
        },
      };
    }
    return null;
  }, [item.product_id, item.item_name, item.list_price, proposalCurrency]);

  const handleSelectChange = (value, option) => {
    if (value == null) {
      onProductSelect(null);
      return;
    }
    onProductSelect({
      value: option.value,
      label: option.label,
      product: option.product,
    });
  };

  if (isCustom) {
    return (
      <div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          Custom item name
        </Text>
        <Input
          value={item.item_name}
          onChange={(e) => onItemChange(index, 'item_name', e.target.value)}
          placeholder="Enter custom item name"
          data-testid={`item-name-${index}`}
        />
      </div>
    );
  }

  return (
    <div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        Product
      </Text>
      <Select
        showSearch
        allowClear
        filterOption={false}
        placeholder="Search for a product..."
        notFoundContent={
          fetching ? 'Searching...' : 'Type at least 2 characters to search'
        }
        options={
          selectedOption &&
          !options.some((o) => o.value === selectedOption.value)
            ? [selectedOption, ...options]
            : options
        }
        value={selectedOption?.value}
        onSearch={debouncedSearch}
        onChange={handleSelectChange}
        style={{ width: '100%' }}
        data-testid={`product-select-${index}`}
      />
    </div>
  );
};

export default ProductSelector;
